import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../config/api';
import { 
  masterPanel, 
  masterFormContainer, 
  masterFormGrid3, 
  masterFormGridRow, 
  masterInputLabel, 
  masterInput, 
  masterSelect, 
  masterAccentButton, 
  masterPrimaryButton,
  masterSectionTitle, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableRow, 
  masterTableData, 
  masterTableID, 
  masterActiveColor, 
  masterInactiveColor, 
  masterTableWrap,
  masterEditButton,
  masterCancelButton,
  masterTableHeaderCell,
  masterTableHeaderCellCenter,
  masterTableStatusCell,
  masterTableActionCell,
  masterFormActions
} from '../Pages/MasterCssClass';

const SessionMaster = () => {
  const [sessions, setSessions] = useState([]);
  const [showList, setShowList] = useState(false);
  const [editId, setEditId] = useState(null);
  
  const [formData, setFormData] = useState({
    session: '',
    status: 'Active'
  });

  const fetchSessions = async () => {
    try {
      const res = await api.get('/sessions');
      setSessions(res.data);
    } catch (err) {
      console.error('Error fetching sessions', err);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleSessionChange = (e) => {
    let val = e.target.value.replace(/[^\d]/g, '');
    if (val.length > 8) val = val.slice(0, 8);
    
    // Automatically insert hyphen after 4 digits
    if (val.length > 4) {
      val = `${val.slice(0, 4)}-${val.slice(4)}`;
    }
    
    setFormData({ ...formData, session: val });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const sessionRegex = /^(\d{4})-(\d{4})$/;
    const match = formData.session.trim().match(sessionRegex);

    if (!match) {
      toast.error('Session must be in YYYY-YYYY format (e.g., 2024-2025)', { id: 'session-val-error' });
      return;
    }

    const startYear = parseInt(match[1], 10);
    const endYear = parseInt(match[2], 10);

    if (startYear >= endYear) {
      toast.error('Start year must be less than end year (e.g., 2021-2022, not 2022-2021)', { id: 'session-val-error' });
      return;
    }

    // Frontend duplicate check
    const isDuplicate = sessions.some(
      (s) => s.session === formData.session && s._id !== editId
    );
    if (isDuplicate) {
      toast.error('This session already exists in the list', { id: 'session-dup-error' });
      return;
    }

    try {
      if (editId) {
        await api.put(`/sessions/${editId}`, formData);
        toast.success('Session updated successfully!', { id: 'session-success' });
      } else {
        const res = await api.post('/sessions', formData);
        toast.success(`Session saved successfully with ID: ${res.data.sessionId}`, { id: 'session-success' });
      }

      setFormData({ session: '', status: 'Active' });
      setEditId(null);
      fetchSessions();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      toast.error(`Error saving session record: ${errorMsg}`, { id: 'session-api-error' });
    }
  };

  const handleEdit = (sessionItem) => {
    setFormData({
      session: sessionItem.session || '',
      status: sessionItem.status || 'Active'
    });
    setEditId(sessionItem._id);
    setShowList(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;
    try {
      await api.delete(`/sessions/${id}`);
      toast.success('Session deleted successfully', { id: 'session-delete' });
      fetchSessions();
    } catch (err) {
      toast.error('Failed to delete session record', { id: 'session-delete-error' });
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-end px-1">
        <button 
          onClick={() => setShowList(!showList)} 
          className={masterPrimaryButton}
        >
          {showList ? 'Add New Session (Form)' : `View List (${sessions.length})`}
        </button>
      </div>

      {!showList ? (
        <div className={masterPanel}>
          <div className={masterFormContainer}>
            <h2 className={masterSectionTitle}>
              {editId ? 'Edit Session Master' : 'Session Master Entry (Auto ID Generation)'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-2">
              <div className={masterFormGrid3}>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Session</label>
                  <input 
                    type="text" 
                    value={formData.session} 
                    onChange={handleSessionChange}
                    className={masterInput} 
                    placeholder="2024-2025"
                    maxLength="9"
                    required 
                  />
                </div>
                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Status</label>
                  <select 
                    value={formData.status} 
                    onChange={(e) => setFormData({...formData, status: e.target.value})} 
                    className={masterSelect}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className={masterFormActions}>
                {editId && (
                  <button 
                    type="button" 
                    onClick={() => { setEditId(null); setFormData({ session: '', status: 'Active' }); }}
                    className={masterCancelButton}
                  >
                    Cancel
                  </button>
                )}
                <button type="submit" className={masterAccentButton}>
                  {editId ? 'Update Session' : 'Save Session'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className={masterPanel}>
          <div className={masterTableWrap}>
            <table className={masterTable}>
              <thead>
                <tr className={masterStickyHeadRow}>
                  <th className={masterTableHeaderCell}>Session ID</th>
                  <th className={masterTableHeaderCell}>Session</th>
                  <th className={masterTableHeaderCellCenter}>Status</th>
                  <th className={masterTableHeaderCellCenter}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s._id} className={masterTableRow}>
                    <td className={masterTableID}>{s.sessionId}</td>
                    <td className={masterTableData}>{s.session || 'N/A'}</td>
                    <td className={masterTableStatusCell}>
                      <span className={s.status === 'Active' ? masterActiveColor : masterInactiveColor}>
                        {s.status}
                      </span>
                    </td>
                    <td className={masterTableActionCell}>
                      <div className="flex justify-center gap-1">
                        <button onClick={() => handleEdit(s)} className={masterEditButton}>
                          Edit
                        </button>
                        <button onClick={() => handleDelete(s._id)} className="px-2 py-0.5 bg-red-500 text-white rounded text-[10px] font-bold uppercase">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionMaster;